// Use for defining 'data' prop in Table component
import { Maybe } from "@figurepos/fgr-lib-backend/lib/types"
import { ExpandedState, PaginationState, RowSelectionState, SortingState } from "@tanstack/react-table"

export type TableRowData<TData = unknown> = TData & {
    id: string
    children?: Maybe<TableRowData<TData>[]>
    isSelectable?: boolean
}

// Use for defining 'columns' prop in Table component
export type TableColumn<TRowData extends TableRowData> = {
    id: string
    getData?: (row: TRowData) => any // Function to get data for filtering and sorting
    cell: (row: TRowData, rowIndex: number) => string | React.ReactNode
    header?: string | React.ReactNode | ((row: TRowData[]) => string | React.ReactNode)
    footer?: string | React.ReactNode | ((row: TRowData[]) => string | React.ReactNode)
    columns?: TableColumn<TRowData>[]
    align?: TableColumnAlign
    width?: number | string // "50%" or 100 (in pixels)
    maxWidth?: number // Maximum width in pixels
    sorting?: TableSortingType<TRowData>
    sortDescFirst?: boolean
    columnClassName?: string
    filterComponent?: () => React.ReactNode
}

export type TableColumnAlign = "left" | "center" | "right"

export type TableSortingType<TRowData> =
    | "alphanumeric"
    | "alphanumericCaseSensitive"
    | "text"
    | "textCaseSensitive"
    | "datetime"
    | "basic"
    | ((rowA: TRowData, rowB: TRowData) => number)

export enum SortDirection {
    asc = "asc",
    desc = "desc",
}

export type TableSize = "small" | "normal" | "large"

export type TableStyle = "dashed" | "normal" | "legacy"

export type TableLoadingMode = "default" | "skeletons"

export enum TableRowSelectVisibility {
    Always = "always",
    Hover = "hover",
}

export type TableVirtualizationConfig = {
    isEnabled: boolean
    overscanCount?: number
    rowHeight: number
    containerHeight: string | number
}

export type TableSortingState = {
    sorting: SortingState
    setSorting: (sorting: SortingState) => void
}

export type TableExpandedState = {
    expanded: ExpandedState
    setExpanded: (expanded: ExpandedState) => void
}

export type TableRowSelectionState = {
    rowSelection: RowSelectionState
    setRowSelection: (rowSelection: RowSelectionState) => void
}

export type TablePaginationState = {
    totalCount: number // Total number of items in the dataset, used for pagination calculation
    pagination: PaginationState
    setPagination: (pagination: PaginationState) => void
}
